import { useEffect, useState, useMemo } from "react";
import { Skeleton, Dropdown, Drawer, Image } from "../../components/ui";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import {
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  Sparkles,
  SearchX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteActivity,
  useGetAllActivities,
  useUpdateActivity,
} from "../../query/activityQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { UPLOADS_URL } from "../../config/constants";
import { getActivityImageSrc } from "./activityHelpers";
import DataBulkToolbar from "../../components/DataTable/DataBulkToolbar";
import DataTableCards from "../../components/DataTable/DataTableCards";
import { confirmBulkAction, runBulkIds, showBulkResult } from "../../utils/bulkActions";
import "../teachers/teachers.css";
import "./activities.css";
import FilterSelect from "../../components/FilterSelect";

function statusTone(status) {
  return status === "ACTIVE" ? "is-active" : "is-inactive";
}

function statusLabel(status) {
  return status === "ACTIVE" ? "Active" : "Inactive";
}

const ACTIVITY_STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

function snippet(text = "", max = 100) {
  const value = String(text || "").trim();
  if (!value) return "—";
  return value.length > max ? `${value.slice(0, max).trim()}…` : value;
}

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function buildStatusFormData(item, status) {
  const formObject = new FormData();
  formObject.append("title", item.title || "");
  formObject.append("description", item.description || "");
  formObject.append("status", status);
  return formObject;
}

function Activities() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: "10",
    keyword: "",
    status: "",
  });
  const debouncedKeyword = useDebouncedValue(searchTerm.trim(), 400);

  const queryParams = useMemo(
    () => ({
      page: String(paginationConfig.pageNumber),
      limit: String(paginationConfig.limit),
      keyword: paginationConfig.keyword,
      status: paginationConfig.status,
    }),
    [paginationConfig]
  );

  const { isLoading, isFetching, data: activities } = useGetAllActivities(queryParams);

  const docs = activities?.docs || [];
  const pageSize = Number(paginationConfig.limit) || 10;
  const currentPage = Number(paginationConfig.pageNumber) || 1;
  const totalDocs = activities?.totalDocs || 0;
  const totalPages = Math.max(1, Math.ceil(totalDocs / pageSize));
  const from = totalDocs === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const to = Math.min(currentPage * pageSize, totalDocs);
  const allSelected = docs.length > 0 && docs.every((row) => selectedIds.includes(row._id));
  const hasFilters = Boolean(paginationConfig.status);

  const { mutate: updateActivity, mutateAsync: updateActivityAsync, isPending: isUpdatingStatus } =
    useUpdateActivity();
  const { mutate: deleteActivity, mutateAsync: deleteActivityAsync } = useDeleteActivity();
  const [isBulkRunning, setIsBulkRunning] = useState(false);

  useEffect(() => {
    setPaginationConfig((prev) =>
      prev.keyword === debouncedKeyword ? prev : { ...prev, keyword: debouncedKeyword, pageNumber: 1 }
    );
  }, [debouncedKeyword]);

  useEffect(() => {
    if (!open) return;
    setSelectedStatus(paginationConfig.status || "");
  }, [open, paginationConfig.status]);

  const applyFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: selectedStatus || "",
    }));
    setOpen(false);
  };

  const resetFilter = () => {
    setSelectedStatus("");
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: "",
    }));
    setOpen(false);
  };

  const handleStatus = (item, status) => {
    if (!item?._id || status === item.status) return;
    updateActivity(
      { id: item._id, data: buildStatusFormData(item, status) },
      {
        onSuccess: () => swal("Success", `Status updated to ${statusLabel(status)}`, "success"),
        onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
      }
    );
  };

  const handleDelete = (item) => {
    swal({
      title: "Are you sure?",
      text: "Do you want to delete this activity?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteActivity(item._id, {
        onSuccess: () => swal("Success", "Activity deleted successfully", "success"),
        onError: (error) => swal("Cannot delete", error?.message || "Failed to delete activity", "error"),
      });
    });
  };

  const handleBulkStatus = async (status) => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: `Mark as ${statusLabel(status).toLowerCase()}`,
      count: selectedIds.length,
      entityLabel: "activities",
    });
    if (!confirmed) return;

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(selectedIds, async (id) => {
      const item = docs.find((row) => row._id === id);
      if (!item) throw new Error("Activity not found");
      await updateActivityAsync({ id, data: buildStatusFormData(item, status) });
    });
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel: "Status update", success, failed });
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: "Delete selected",
      count: selectedIds.length,
      entityLabel: "activities",
    });
    if (!confirmed) return;

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(selectedIds, deleteActivityAsync);
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel: "Delete", success, failed });
  };

  return (
    <div className="data-page activities-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>
          <button
            type="button"
            className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
            aria-label="Filter"
            onClick={() => setOpen(true)}
          >
            <Filter size={18} />
            {hasFilters ? <span className="data-filter-dot" /> : null}
          </button>

          <DataBulkToolbar
            selectedCount={selectedIds.length}
            onClear={() => setSelectedIds([])}
            disabled={isBulkRunning || isUpdatingStatus}
            actions={[
              { key: "active", label: "Mark as active", onClick: () => handleBulkStatus("ACTIVE") },
              { key: "inactive", label: "Mark as inactive", onClick: () => handleBulkStatus("INACTIVE") },
              { key: "delete", label: "Delete selected", tone: "danger", onClick: handleBulkDelete },
            ]}
          />
        </div>

        <div className="data-toolbar-actions">
          <button type="button" className="data-primary" onClick={() => navigate("/activities/addActivity")}>
            <Plus size={18} />
            New Activity
          </button>
        </div>
      </div>

      <div className="data-card">
        {isLoading && !activities ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : docs.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {paginationConfig.keyword || hasFilters ? (
                <SearchX size={36} strokeWidth={1.6} />
              ) : (
                <Sparkles size={36} strokeWidth={1.6} />
              )}
            </span>
            <h3>{paginationConfig.keyword || hasFilters ? "No activities found" : "No activities yet"}</h3>
            <p>
              {paginationConfig.keyword || hasFilters
                ? "Try a different keyword or clear filters."
                : "Add a post category to see it listed here."}
            </p>
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching || isUpdatingStatus}>
              <table className="data-table has-row-check">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="data-check"
                        checked={allSelected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds((prev) => Array.from(new Set([...prev, ...docs.map((row) => row._id)])));
                          } else {
                            const pageIds = new Set(docs.map((row) => row._id));
                            setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
                          }
                        }}
                      />
                    </th>
                    <th>Title</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((item) => {
                    const selected = selectedIds.includes(item._id);
                    const title = item.title || "—";
                    const imageSrc = getActivityImageSrc(item, UPLOADS_URL);
                    const actionItems = [
                      {
                        key: "1",
                        label: <div style={{ padding: "8px 12px" }}>View</div>,
                        onClick: () => navigate(`/activities/${item._id}`),
                      },
                      {
                        key: "2",
                        label: <div style={{ padding: "8px 12px" }}>Edit</div>,
                        onClick: () => navigate(`/activities/edit/${item._id}`),
                      },
                      {
                        key: "3",
                        label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Delete</div>,
                        onClick: () => handleDelete(item),
                      },
                    ];

                    return (
                      <tr key={item._id} className={selected ? "is-selected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            className="data-check"
                            checked={selected}
                            onChange={(e) =>
                              setSelectedIds((prev) =>
                                e.target.checked ? [...prev, item._id] : prev.filter((id) => id !== item._id)
                              )
                            }
                          />
                        </td>
                        <td>
                          <div className="act-title-cell">
                            {imageSrc ? (
                              <span className="act-thumb-wrap is-preview">
                                <Image className="act-thumb" src={imageSrc} alt={title} />
                              </span>
                            ) : (
                              <span className="act-thumb-wrap is-empty">—</span>
                            )}
                            <p className="data-name">{title}</p>
                          </div>
                        </td>
                        <td>
                          <p className="act-desc-snippet">{snippet(item.description)}</p>
                        </td>
                        <td>
                          <Dropdown
                            menu={{
                              items: ACTIVITY_STATUSES.map((option) => ({
                                key: option.value,
                                label: (
                                  <div className="data-status-option">
                                    <span className={`data-status-dot ${statusTone(option.value)}`} />
                                    {option.label}
                                    {item.status === option.value ? <Check size={14} /> : null}
                                  </div>
                                ),
                                onClick: () => handleStatus(item, option.value),
                              })),
                            }}
                            trigger={["click"]}
                            placement="bottomLeft"
                          >
                            <button
                              type="button"
                              className={`data-pill data-status-trigger ${statusTone(item.status)}`}
                              aria-label="Change status"
                            >
                              {statusLabel(item.status)}
                              <ChevronDown size={12} />
                            </button>
                          </Dropdown>
                        </td>
                        <td>{item.createdAt ? dayjs(item.createdAt).format("MMMM D, YYYY") : "—"}</td>
                        <td>
                          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
                            <button type="button" className="data-action" aria-label="More actions">
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} from {totalDocs} data
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() =>
                    setPaginationConfig((prev) => ({ ...prev, pageNumber: Math.max(1, currentPage - 1) }))
                  }
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                    onClick={() => setPaginationConfig((prev) => ({ ...prev, pageNumber: page }))}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() =>
                    setPaginationConfig((prev) => ({
                      ...prev,
                      pageNumber: Math.min(totalPages, currentPage + 1),
                    }))
                  }
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Status</p>
          <FilterSelect
            multiple
            value={selectedStatus}
            onChange={setSelectedStatus}
            options={[
              { value: "", label: "All statuses" },
              { value: "ACTIVE", label: "Active" },
              { value: "INACTIVE", label: "Inactive" },
            ]}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default Activities;
